import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { TenantViewModel } from '../tenant-list-table/tenant-list-table.component';

/** ダイアログのViewModel */
export type TenantEditorDialogViewModel = {
  updating: boolean;
  tenantList: TenantViewModel[];
  target?: EditedTenantViewModel;
};

/** テナントのViewModel */
export type EditedTenantViewModel = {
  /** テナントID */
  tenantId?: string;

  /** テナント名 */
  tenantName: string;

  /** 会社名 */
  companyName: string;

  /** 説明 */
  description: string;

  /** 会社ロゴ */
  companyLogoPath?: string;
};

/** テナントのフォームグループ */
type TenantFormGroup = FormGroup<{
  /** テナント名 */
  tenantName: FormControl<string>;

  /** 会社名 */
  companyName: FormControl<string>;

  /** 説明 */
  description: FormControl<string>;

  /** 会社ロゴ  */
  // companyLogoPath: FormControl<string>;
}>;

@Component({
  selector: 'app-tenant-edit-dialog',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIconModule,
    CommonModule,
  ],
  templateUrl: './tenant-edit-dialog.component.html',
  styleUrl: './tenant-edit-dialog.component.scss',
})
export class TenantEditDialogComponent {
  /** 更新中 */
  public updating: boolean;

  /** 更新対象のテナントID */
  private targetTenantId?: string;

  /** フォームグループ */
  public formGroup: TenantFormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: TenantEditorDialogViewModel,
    private dialogRef: MatDialogRef<TenantFormGroup>,
    private dialog: MatDialog,
  ) {
    this.updating = options.updating;
    this.targetTenantId = options.target?.tenantId;

    this.formGroup = new FormGroup({
      tenantName: new FormControl<string>(options.target?.tenantName || '', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(128),
          this.tenantDuplicationValidator(
            options.tenantList,
            options.target?.tenantName,
          ),
        ],
      }),
      companyName: new FormControl<string>(options.target?.companyName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(128)],
      }),
      description: new FormControl<string>(options.target?.description || '', {
        nonNullable: true,
        validators: [Validators.maxLength(1024)],
      }),
      // companyLogoPath: new FormControl<string>('', {
      //   nonNullable: true,
      //   validators: [Validators.required],
      // }),
    });
  }

  /**
   * 送信
   */
  public async submit() {
    const tenantName = this.formGroup.get('tenantName')?.value || '';
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `テナント「${tenantName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const tenant: EditedTenantViewModel = {
      tenantId: this.targetTenantId,
      tenantName: this.formGroup.get('tenantName')?.value || '',
      companyName: this.formGroup.get('companyName')?.value || '',
      description: this.formGroup.get('description')?.value || '',
      companyLogoPath: '',
    };
    this.dialogRef.close(tenant);
  }

  /** テナント名の重複バリデーション
   * @param tenantList テナント一覧
   * @param originTenantName 元のテナント名
   * @returns テナント名重複バリデータ
   */
  private tenantDuplicationValidator(
    tenantList: TenantViewModel[],
    originTenantName: string | undefined,
  ): ValidatorFn {
    const validator: ValidatorFn = (
      control: AbstractControl,
    ): ValidationErrors | null => {
      const tenantName = control?.value;

      const isDuplicated: boolean = tenantList.some(
        (tenant) =>
          tenantName === tenant.tenantName && tenantName != originTenantName,
      );

      if (isDuplicated && !control?.errors) {
        control?.setErrors({ isDuplicated: true });
      }

      return isDuplicated ? { isDuplicated: true } : null;
    };
    return validator;
  }
}
